import React, { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import Layout from 'components/Layout';
import { PageContextType } from 'types';
import { AuthenticationProvider } from 'components/Authentication';
import { AppContext } from '../context/AppContext';
import { getGDPRStatus } from '../helpers/geolocation';
import { PageDataType, PageNotFoundDataType } from './types';

const textToBooleanMapping: { [index: string]: boolean } = {
    yes: true,
    no: false,
};
type AllProvidersProps = {
    page: PageDataType | PageNotFoundDataType;
    pageContext: PageContextType;
};

function isNormalPage(page: PageDataType | PageNotFoundDataType): page is PageDataType {
    return !!(page as PageDataType).body;
}

const getAppProviderOptions = (page: PageDataType | PageNotFoundDataType) => {
    return isNormalPage(page)
        ? {
              show_navigation_header: textToBooleanMapping[page.show_navigation_header],
              header_type: page.header_type,
              partner_logo: page.partner_logo,
              partner_url: page.partner_url,
              show_footer: textToBooleanMapping[page.show_footer],
          }
        : {
              show_navigation_header: true,
              header_type: 'Main Navigation',
              partner_logo: null,
              partner_url: null,
              show_footer: true,
          };
};

export const AllProviders: React.FC<AllProvidersProps> = ({ page, pageContext, children }) => {
    // Setting this to true by default so there is no flickering in the UI
    const [hasGivenCookieConsent, setHasGivenCookieConsent] = useState(true);
    const options = getAppProviderOptions(page);

    // Unpublished pages don't contain data at compile time, so we need defaults here.
    const layoutData = {
        alternateLanguages: page._meta.alternateLanguages || [],
        homePage: (isNormalPage(page) && page.home_page) || '',
        lang: page._meta.lang || 'en-us',
    };

    const handleAgreeToCookieConsent = useCallback(() => {
        Cookies.set('cookieconsent_status', 'dismiss', {
            path: '/',
            domain: process.env.COOKIE_DOMAIN,
            expires: 365,
        });
        setHasGivenCookieConsent(true);
    }, []);

    useEffect(() => {
        const geolocation$ = getGDPRStatus().subscribe((isPartOfGDPR: boolean) => {
            const hasDismissedBanner = Cookies.get('cookieconsent_status') === 'dismiss';
            // If the user is part of the GDPR, then show the cookie consent banner
            // if they haven't already accepted the terms
            if (isPartOfGDPR && !hasDismissedBanner) {
                setHasGivenCookieConsent(false);
            }
        });

        return () => {
            geolocation$.unsubscribe();
        };
    }, []);

    return (
        <AppContext.Provider
            value={{
                pageContext: pageContext,
                alternateLanguages: layoutData.alternateLanguages,
                options,
                homePage: layoutData.homePage,
            }}
        >
            <AuthenticationProvider>
                <Layout
                    // TODO: remove these props and use app context instead
                    lang={layoutData.lang}
                    alternateLanguages={layoutData.alternateLanguages}
                    options={options}
                    homePage={layoutData.homePage}
                    pageContext={pageContext}
                    hasGivenCookieConsent={hasGivenCookieConsent}
                    onAgreeToCookieConsent={handleAgreeToCookieConsent}
                >
                    {children}
                </Layout>
            </AuthenticationProvider>
        </AppContext.Provider>
    );
};
